<template>
<div id="permissions">
  <div id="permissions-states" class="permissions layout--base">
    <div class="layout--base__header">
      <a-logo />
    </div>
    <div class="layout--base__content">
      <div class="layout--base__content__wrapper">
        <h2 class="text--center">{{ $t('permissions.title') }}</h2>
        <p class="text--center mb--3">{{ $t('permissions.text') }}</p>
        <permissions-item v-if="isCameraRecommended" device="camera" />
        <permissions-item v-if="isMicrophoneRecommended" device="microphone" />
        <permissions-item v-if="isLocationRecommended" device="location" />
        <div class="d--flex justify-content--end">
          <div 
            v-if="state.matches('ui.insufficientPermissions')"
          >
            <a-button
              variant="link-primary"
              class="me--3"
              @click="send('continue')"
            >
              {{ $t('permissions.actions.continue')}}
            </a-button>
          </div>
          <div 
            v-if="state.matches('ui.insufficientPermissions')"
          >
            <a-button
              @click="send('init')"
            >
              {{ $t('permissions.actions.tryAgain')}}
            </a-button>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>
</template>

<script setup>
import { computed, nextTick } from 'vue'
import { usePermissionsMachine } from '@/state'
import { useAppStore, useUserStore } from '@/stores'
import { useLocalStream, useLocation, useMediaPermissions } from '@/composables'
import {androidConnector} from '@communication/remotes/AndroidConnector'

const app = useAppStore()
const user = useUserStore()
const { stream: localStream } = useLocalStream()
const { getLocation } = useLocation()
const isCameraRecommended = app.isCameraRecommended
const isMicrophoneRecommended = app.isMicrophoneRecommended
const isLocationRecommended = app.isLocationRecommended
const { state, send, service } = usePermissionsMachine()
const hasCameraPermission = computed(() => state.value.context.hasCameraPermission)
const hasMicrophonePermission = computed(() => state.value.context.hasMicrophonePermission)
const { requestMediaStreams, requestAndroidMediaStreams, requestIndividualStreams } = useMediaPermissions()

const handleGetMediaStreams = async () => {
  try {
    let result
    const isLinuxAndSamsungBrowser = /Linux/.test(navigator.userAgent) && /SamsungBrowser/.test(navigator.userAgent)

    if (androidConnector.connected) {
      // Android devices
      result = await requestAndroidMediaStreams({
        requestCamera: isCameraRecommended,
        requestMicrophone: isMicrophoneRecommended
      })
    } else if (isLinuxAndSamsungBrowser) {
      // Linux devices
      result = await requestIndividualStreams({
        requestCamera: isCameraRecommended,
        requestMicrophone: isMicrophoneRecommended
      })
    } else {
      // Browser-based devices
      result = await requestMediaStreams({
        requestCamera: isCameraRecommended,
        requestMicrophone: isMicrophoneRecommended
      })
    }

    // Update app state based on results
    let cameraGranted = false
    let microphoneGranted = false

    // Handle camera result
    if (isCameraRecommended) {
      if (result.camera.granted) {
        app.transition('CAMERA_AVAILABLE')
        user.profile.camera = 'on'
        cameraGranted = true

        // No MUTE_CAMERA here: the camera always starts unmuted. track.muted is the browser's
        // data-flow flag, not a mute intent — WebRTC tracks from the Android connector report
        // muted=true until the first frames arrive, which used to false-positive this check.

        // Check torch capability for camera — must not fail the whole media flow:
        // ImageCapture/getPhotoCapabilities is flaky on some devices
        try {
          const hasTorchCapability = await localStream.hasTorchCapability()
          user.setTorchCapability(hasTorchCapability)
        } catch (error) {
          console.error('[handleGetMediaStreams] Torch capability check failed:', error)
          user.setTorchCapability(false)
        }
      } else {
        app.transition('CAMERA_NOT_AVAILABLE')
      }
    }

    // Handle microphone result
    if (isMicrophoneRecommended) {
      if (result.microphone.granted) {
        app.transition('MICROPHONE_AVAILABLE')
        user.profile.microphone = 'on'
        microphoneGranted = true

        // The microphone also starts unmuted here; its start-muted state is governed by the
        // session's allowStartMuted flag, applied in JoinView (MUTE_MICROPHONE, isSilent).
      } else {
        app.transition('MICROPHONE_NOT_AVAILABLE')
      }
    }

    // Send appropriate event to state machine
    if (cameraGranted && microphoneGranted) {
      send('mediaStreamsSuccess')
    } else if (cameraGranted || microphoneGranted) {
      send({
        type: 'mediaStreamsPartialSuccess',
        cameraGranted,
        microphoneGranted
      })
    } else {
      send('mediaStreamsDenied')
    }
  } catch (error) {
    console.error('[handleGetMediaStreams] Error:', error)
    app.transition('CAMERA_NOT_AVAILABLE')
    app.transition('MICROPHONE_NOT_AVAILABLE')
    send('mediaStreamsDenied')
  }
}

const handleGetLocation = () => {
  getLocation()
    .then((location) => {
      user.setLocation(location)
      location.denied ? send('locationDenied') : send('locationSuccess')
    })
    .catch(() => send('locationDenied'))
}


const handleValidatePermissions = () => {
  // Require at least ONE of camera or microphone if either is recommended
  const hasAtLeastOneMedia = hasCameraPermission.value || hasMicrophonePermission.value
  const isMediaRecommended = isCameraRecommended || isMicrophoneRecommended
  
  if (isMediaRecommended && !hasAtLeastOneMedia) {
    send('insufficientPermissions')
    return
  }
  
  send('sufficientPermissions')
}

const stateHandlersMap = new Map([
  ['init', async () => {
    app.loaded()
    const result = await androidConnector.connect()
    send('media')
  }],
  ['media', () => (isCameraRecommended || isMicrophoneRecommended) ? send('mediaRecommended') : send('mediaNotRecommended')],
  ['getMediaStreams', () => handleGetMediaStreams()],
  ['location', () => isLocationRecommended ? send('locationRecommended') : send('locationNotRecommended')],
  ['getLocation', () => handleGetLocation()],
  ['validatePermissions', () => nextTick(() => handleValidatePermissions())],
  ['join', () => setTimeout(() => {
    app.transition('join')
  }, 1000)],
  ['insufficientPermissions', () => {}]
])

service.subscribe((state) => stateHandlersMap.get(state.value.ui)())
</script>

<style lang="scss">
$permissions-bg: $body-bg;
$permissions-loader-duration: 1s;
$permissions-opacity: 0.5;
$permissions-asking-opacity: 1;
$permissions-asking-transform: 1.15;
$permissions-granted-opacity: 1;
$permissions-granted-transform: 1;

.permissions {
  z-index: ($zindex-modal - 1);
  background: $permissions-bg;

  .box {
    transition: transform $duration-fast, opacity $duration-fast;
    opacity: $permissions-opacity;

    &__content {
      padding-bottom: 0;
    }

    &.--denied {
      animation: shake $duration-slow linear;
      animation-delay: ($duration-fast * 1.25);
    }

    &.--granted,
    &.--denied,
    &.--connected {
      transform: scale($permissions-granted-transform);
      opacity: $permissions-granted-opacity;
    }

    &.--asking {
      color: $body-color;
      transform: scale($permissions-asking-transform);
      opacity: $permissions-asking-opacity;
    }

    &.--connecting {
      transform: scale($permissions-asking-transform);
      opacity: $permissions-asking-opacity;
    }
  }

  &__modal {
    &.visible {
      z-index: ($zindex-modal - 1);
      background: $backdrop;
    }
  }

  @media (max-width: $breakpoint-sm) {
    .layout--base__content {
      padding-top: 0;
    }
  }
}

.permission {
  display: flex;
  padding: map-get($spacers, 3) 0;

  &__icon {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-right: map-get($spacers, 3);
  }

  &__wrapper {
    width: 100%;
  }

  &__content {
    text-align: left;
    margin: 0;
  }

  &__header {
    display: flex;
    justify-content: space-between;

    h4 {
      margin-bottom: 0;
    }
  }

  @media (max-width: $breakpoint-sm) {
    padding: map-get($spacers, 2) 0;

    &__header {
      display: block;
    }
  }
}
</style>
